import React from "react";
import { useAutoPoll } from "./useAutoPoll";
import { backendUrl } from "@/lib/seoApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Settings, ExternalLink } from "lucide-react";

const GROUPS = [
  {
    key: "general", label: "General",
    fields: [
      { path: "environment_label", label: "Environment" },
      { path: "timezone",          label: "Timezone" },
      { path: "operator_email",    label: "Operator email" },
    ],
  },
  {
    key: "research", label: "Research (DataForSEO)",
    fields: [
      { path: "dataforseo_enabled",       label: "DataForSEO enabled", boolean: true },
      { path: "dataforseo_timeout_seconds", label: "DFS timeout (s)" },
      { path: "default_location_code",     label: "Default location code" },
      { path: "default_language_code",     label: "Default language" },
      { path: "keyword_min_search_volume", label: "Min search volume" },
      { path: "serp_top_n",                label: "SERP top N" },
    ],
  },
  {
    key: "llm", label: "LLM",
    fields: [
      { path: "default_provider",     label: "Provider" },
      { path: "default_model",        label: "Model" },
      { path: "temperature",          label: "Temperature" },
      { path: "max_tokens",           label: "Max tokens" },
      { path: "timeout_seconds",      label: "Timeout (s)" },
      { path: "retry_max_attempts",   label: "Retry max attempts" },
      { path: "retry_backoff_seconds", label: "Retry backoff (s)" },
    ],
  },
  {
    key: "images", label: "Images",
    fields: [
      { path: "images_enabled",         label: "Images enabled", boolean: true },
      { path: "provider",               label: "Provider" },
      { path: "max_images_per_article", label: "Max per article" },
      { path: "style_guide",            label: "Style guide", truncate: 60 },
    ],
  },
  {
    key: "validation", label: "Validation",
    fields: [
      { path: "min_words",                 label: "Min words" },
      { path: "max_words",                 label: "Max words" },
      { path: "min_uniqueness_pct",        label: "Min uniqueness %" },
      { path: "uniqueness_gate_enabled",   label: "Uniqueness gate (pre-publish)", boolean: true },
      { path: "prose_uniqueness_threshold", label: "Prose uniqueness threshold %" },
      { path: "max_rewrite_iterations",    label: "Max auto-rewrite iterations" },
      { path: "textru_enabled",            label: "Text.ru enabled", boolean: true },
      { path: "textru_timeout_seconds",    label: "Text.ru timeout (s)" },
      { path: "require_headings_hierarchy", label: "Enforce H1/H2 hierarchy", boolean: true },
      { path: "forbidden_phrases_check",    label: "Forbidden phrases check", boolean: true },
    ],
  },
  {
    key: "publishing", label: "Publishing",
    fields: [
      { path: "auto_publish",       label: "Auto-publish", boolean: true },
      { path: "draft_by_default",   label: "Draft by default", boolean: true },
      { path: "republish_allowed",  label: "Republish allowed", boolean: true },
      { path: "publish_target",     label: "Publish target" },
    ],
  },
  {
    key: "budgets", label: "Budgets",
    fields: [
      { path: "day_limit_usd",     label: "Day limit ($)" },
      { path: "week_limit_usd",    label: "Week limit ($)" },
      { path: "month_limit_usd",   label: "Month limit ($)" },
      { path: "hard_budget_limit", label: "Hard limit block", boolean: true },
      { path: "warn_at_pct",       label: "Warn at %" },
    ],
  },
  {
    key: "notifications", label: "Notifications",
    fields: [
      { path: "email_on_failure",        label: "Email on failure", boolean: true },
      { path: "email_on_budget_warning", label: "Email on budget warning", boolean: true },
      { path: "notify_email",            label: "Notify email" },
    ],
  },
  {
    key: "advanced", label: "Advanced",
    fields: [
      { path: "workflow_stage_retention_days", label: "Stage retention (days)" },
      { path: "event_log_max_entries",         label: "Event log max entries" },
      { path: "debug_prompts",                 label: "Debug prompts", boolean: true },
      { path: "request_id_header",             label: "Request ID header" },
    ],
  },
];

function fmt(v, field) {
  if (v === null || v === undefined || v === "") return <span className="text-neutral-400">—</span>;
  if (field.boolean) {
    return v ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">ON</Badge>
              : <Badge variant="secondary">OFF</Badge>;
  }
  if (field.truncate && typeof v === "string" && v.length > field.truncate)
    return v.slice(0, field.truncate) + "…";
  return String(v);
}

export default function OpsConfig() {
  const { data, loading, error, refresh } = useAutoPoll(
    backendUrl("/api/seo/ops/config"), { intervalMs: 30000 });

  if (error) return <div className="p-8 text-red-600" data-testid="ops-config-error">{error}</div>;
  if (!data)  return <div className="p-8 text-neutral-500">Loading configuration…</div>;

  const cmsAdminUrl = data.cms_admin_url;
  const loadedFrom = data.loaded_from_cms ? "CMS-driven" : "Defaults (CMS unreachable)";

  return (
    <div className="p-6" data-testid="ops-config-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 flex items-center gap-2">
            <Settings size={22} /> Operations Configuration
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Every operational knob of the pipeline. Editable in Payload CMS — changes
            take effect within 30s. Currently: <b>{loadedFrom}</b>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={refresh} data-testid="config-reload-btn">
            Reload
          </Button>
          <a href={cmsAdminUrl} target="_blank" rel="noreferrer" data-testid="config-edit-btn">
            <Button size="sm" className="gap-2">
              <ExternalLink size={14} /> Edit in CMS
            </Button>
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {GROUPS.map((g) => (
          <Card key={g.key} data-testid={`config-group-${g.key}`}>
            <CardHeader>
              <CardTitle className="text-base">{g.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="text-sm space-y-1.5">
                {g.fields.map((f) => (
                  <div key={f.path} className="flex justify-between gap-4 py-1 border-b border-neutral-100 last:border-0">
                    <dt className="text-neutral-600">{f.label}</dt>
                    <dd className="text-right font-medium text-neutral-900">
                      {fmt((data[g.key] || {})[f.path], f)}
                    </dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-6 text-xs text-neutral-500">
        Cache TTL: {data.cache_ttl_seconds}s ·
        Loaded at: {data.loaded_at_epoch ? new Date(data.loaded_at_epoch * 1000).toLocaleString() : "—"} ·
        <Link to="/seo-studio/ops" className="underline ml-1">Back to Operations</Link>
      </div>
    </div>
  );
}
